import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import InfiniteScroll from "react-infinite-scroll-component";
import {
    TextField,
    IconButton,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    useMediaQuery,
    InputAdornment
} from "@mui/material";
import { defaultVariables } from "../../../config";
import RayaField from "../../components/elements/fields/RayaField";
import '../../../files/css/chat.css'
import {useTranslation} from "react-i18next";
import {theme} from "../../../config/design/theme";
import {Mic,Image,Send} from "lucide-react";
import ChatItem from "../chats/ChatItem";
import {useSnackbar} from "notistack";

const TicketScreen = ({list,is_closed}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [messages, setMessages] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [recording, setRecording] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const chatEndRef = useRef(null);
    const isFetchingRef = useRef(false);
    const token = localStorage.getItem("ACT");

    const fetchMessages = async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        try {
            const response = await axios.get(`${defaultVariables.url}/tickets/get/${list?.id}?page=${page}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.data.length > 0) {
                setMessages((prev) => [...response.data.data.reverse(), ...prev]);
                setPage((prev) => prev + 1);
            }
            setHasMore(response.data.current_page < response.data.last_page);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching messages:", error);
        } finally {
            isFetchingRef.current = false;
        }
    };
    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };
    const sendMessage = async (type, content) => {
        if (!content.trim()) return;
        try {
            const response = await axios.post(`${defaultVariables.url}/tickets/send`,
                {
                    type,
                    message: content,
                    list_id:list.id,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            await setMessages((prev) => [...prev, response.data]);
            await setMessage("");
            await setTimeout(scrollToBottom, 500);
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };
    useEffect(() => {
        fetchMessages().then(() => {
            setTimeout(scrollToBottom, 500);
        });

        // ✅ Auto-refresh messages every 1 minute
        const interval = setInterval(async () => {
            try {
                const response = await axios.get(`${defaultVariables.url}/tickets/get/${list?.id}?page=1`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.data.length > 0) {
                    setMessages((prevMessages) => {
                        const newMessages = response.data.data.reverse();

                        // ✅ Only add messages that are not already in the list
                        const filteredMessages = newMessages.filter(
                            (msg) => !prevMessages.some((m) => m.id === msg.id)
                        );

                        return [...prevMessages, ...filteredMessages];
                    });
                }
            } catch (error) {
                console.error("Error fetching new messages:", error);
            }
        }, 45 * 1000); // Fetch every 45 seconds

        return () => clearInterval(interval); // Cleanup on unmount
    }, []);

    const uploadFile = async (file) => {
        if (!file) return;
        setUploading(true);

        const formData = new FormData();
        const isImage = file.type.startsWith("image");
        formData.append("file", file);
        formData.append("type", isImage ? "image" : "audio");

        try {
            const response = await axios.post(`${defaultVariables.url}/file/add-for-chat`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                },
            });

            sendMessage(isImage ? "image" : "audio", response.data.url);
        } catch (error) {
            enqueueSnackbar(t('wrongFormat'), { variant: 'warning' });
            console.error("Error uploading file:", error.response?.data || error.message);

        } finally {
            setUploading(false);
            setSelectedFile(null);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            uploadFile(file);
        }
    };

    const startRecording = async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.ondataavailable = (event) => audioChunksRef.current.push(event.data);
        mediaRecorderRef.current.onstop = async () => {
            const audioBlob = new Blob(audioChunksRef.current, { type: "audio/mp3" });
            const audioFile = new File([audioBlob], `recording_${Date.now()}.mp3`, { type: "audio/mp3" });
            uploadFile(audioFile);
        };

        audioChunksRef.current = [];
        mediaRecorderRef.current.start();
        setRecording(true);
    };
    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
        }
        setRecording(false);
        mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());
    };

    useEffect(() => {
        fetchMessages().then(() => {
            setTimeout(scrollToBottom, 500);
        });
    }, []);

    return (
        <div style={{ margin: "auto", padding: "10px", borderRadius: "10px"}}>
            <div
                id="scrollableDiv"
                style={{
                    height: isMobile ? "calc(100vh - 250px)" : 500,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    padding: "10px",
                    background: "#fff",
                    border:"1px solid #ddd",
                    borderRadius: "10px"
                }}
            >
                {
                    !loading &&
                    <>
                        {
                            !!messages.length ?
                                <InfiniteScroll
                                    dataLength={messages.length}
                                    next={fetchMessages}
                                    hasMore={hasMore}
                                    inverse={true}
                                    loader={<CircularProgress/>}
                                    scrollableTarget="scrollableDiv"
                                >
                                    {messages.map((msg) => (
                                        <ChatItem key={msg.id} msg={msg}/>
                                    ))}
                                    <div ref={chatEndRef} />
                                </InfiniteScroll>
                                :
                                <div>
                                    <Typography>{t('noMessage')}</Typography>
                                </div>
                        }
                    </>
                }

            </div>

            {
                !is_closed &&
                <div className='chat-actions'>
                    <RayaField
                        style={{background:"#fff", borderRadius: 10}}
                        fullWidth
                        placeholder={t('typeAMessage')}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={uploading}
                        onKeyDown={(e) => e.key === "Enter" && sendMessage("text", message)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            color="primary"
                                            onClick={() => sendMessage("text", message)}
                                            disabled={uploading}
                                        >
                                            <Send />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <div className="actions">
                        <IconButton
                            component="label"
                            disabled={uploading}
                        >
                            <Image />
                            <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                        </IconButton>
                        <IconButton
                            onClick={recording ? stopRecording : startRecording}
                            disabled={uploading}

                        >
                            <Mic className={recording ? "record-button recording" : "record-button"}/>
                        </IconButton>
                    </div>
                </div>
            }

        </div>
    );
};

export default TicketScreen;
